import { formatQuantity, zoneTypeLabel } from '../../utils/inventoryLabels';
import { matchCodes, normalizeCode, sameSlotCode } from '../operator/operatorCodes';

/*
 * The rules of storing on the floor: which pending row a code is, where it
 * should go, and what to say once it went.
 */

export const PUTAWAY_STEP = {
  PRODUCT: 'product',
  DESTINATION: 'destination',
  QUANTITY: 'quantity',
};

export const PUTAWAY_SOURCE = {
  RECEIVING: 'receiving',
  RESTOCK: 'restock',
};

/** Where stock can be stored. */
export const STORABLE_ZONES = ['storage', 'picking'];

/**
 * The pending rows a scanned product code can be: one per origin and lot.
 *
 * @param {Array<object>} rows
 * @param {string} code
 */
export const matchPutawayRows = (rows, code) =>
  rows.filter(
    (row) => matchCodes(row.codes, code) || (row.product_sku && normalizeCode(row.product_sku) === normalizeCode(code))
  );

/**
 * The row being stored: as the list last brought it, or as the task of the
 * tray that opened it did — the list's page may not bring it.
 *
 * @param {object} putaway - The `operatorPutaway` slice.
 */
export const putawayRowOf = ({ list, selectedKey, requested }) => {
  if (!selectedKey) return null;
  const listed = list.rows.find((row) => row.key === selectedKey);
  if (listed) return listed;
  return requested?.row?.key === selectedKey ? requested.row : null;
};

const join = (parts) => parts.filter(Boolean).join(' · ');

/** A pending row as a choice, when a code is more than one of them. */
export const rowOption = (row) => ({
  id: row.key,
  factor: 1,
  badge: null,
  title: `${row.product_sku} — ${row.product_name}`,
  subtitle: join([
    row.slot_name || row.slot_code ? `desde ${row.slot_name || row.slot_code}` : null,
    row.lot ? `lote ${row.lot}` : null,
    `${formatQuantity(row.quantity)} un`,
    row.order_reference ? `orden ${row.order_reference}` : null,
  ]),
});

/** Every place the server suggested for a row, best first. */
export const rowSuggestions = (row) => {
  const all = [row?.suggested, ...(row?.suggestions || [])].filter(Boolean);
  return all.filter((item, index) => all.findIndex((other) => other.slot_code === item.slot_code) === index);
};

/**
 * The destination a scanned slot code is, when it is one of the suggested.
 *
 * @returns {{code: string, name: string, suggested: true}|null}
 */
export const suggestedDestination = (row, code) => {
  const hit = rowSuggestions(row).find((item) => sameSlotCode(item.slot_code, code));
  return hit ? { code: hit.slot_code, name: hit.slot_name, suggested: true } : null;
};

/**
 * Why a slot the scan found cannot take stock, or `null`.
 *
 * @param {{code: string, is_active: boolean, zone_type: string}} slot
 */
export const destinationProblem = (slot) => {
  if (!slot.is_active) return `${slot.code} está inactiva.`;
  if (!STORABLE_ZONES.includes(slot.zone_type)) {
    return `${slot.code} es de ${zoneTypeLabel(slot.zone_type).toLowerCase()}: ahí no se guarda.`;
  }
  return null;
};

/** Why a quantity cannot be stored, written for the button; or `null`. */
export const putawayQuantityProblem = (quantity, row) => {
  const amount = Number(quantity);
  if (!(amount > 0)) return 'Indica cuántos guardas';
  if (!row?.allows_fractional_qty && !Number.isInteger(amount)) return 'Se guarda en unidades enteras';
  if (amount > Number(row?.quantity)) return `Hay ${formatQuantity(row.quantity)} un por guardar`;
  return null;
};

/**
 * The body of a putaway. A received row names its stock bucket, so the server
 * knows the product, the slot and the lot it comes from; a row going back
 * names its order line. `extra` carries what a 409 asked to choose.
 *
 * @param {{source: string, bucketId?: number, orderProductId?: number, lot?: string|null,
 *   toSlotCode: string, quantity: number, extra?: object}} arg
 * @param {string} key
 */
export const buildStoreBody = (arg, key) => {
  const common = { to_slot_code: arg.toSlotCode, quantity: Number(arg.quantity), idempotency_key: key };
  const base =
    arg.source === PUTAWAY_SOURCE.RESTOCK
      ? {
          order_product_id: arg.orderProductId,
          ...(arg.lot != null ? { lot: arg.lot } : {}),
          ...common,
        }
      : { bucket_id: arg.bucketId, ...common };
  return { ...base, ...(arg.extra || {}) };
};

/**
 * Whether a store takes all the row had, as the screen showed it: the task
 * is done.
 *
 * @param {{quantity: number, rowQuantity?: number}} arg - `storePutaway`'s.
 */
export const emptiesPutawayRow = (arg) =>
  arg?.rowQuantity != null && Number(arg.quantity) >= Number(arg.rowQuantity);

/** What to say once stock was stored. */
export const storeMessage = (payload, arg) => {
  if (payload.replayed) return { tone: 'success', message: 'Ese guardado ya estaba registrado.' };
  const message = `${formatQuantity(arg.quantity)} un de ${arg.sku} guardadas en ${arg.toSlotName || arg.toSlotCode}`;
  const warning =
    typeof payload.capacity_warning === 'string' ? payload.capacity_warning : payload.capacity_warning?.message;
  return warning ? { tone: 'warning', message: `${message}. ${warning}` } : { tone: 'success', message };
};
